import React, { useEffect, useState } from "react";
import Select from "react-select";
import Swal from "sweetalert2";
import moment from "moment";
import apiClient from "../utils/apiClient";
import { SERVER_BASE } from "../config/server";
import "bootstrap/dist/css/bootstrap.min.css";
import { useLocation } from "react-router-dom";

const DonationForm = () => {
  const centerCode = localStorage.getItem("code");
  /* ================= RECEIPT STATE ================= */
  const [receiptData, setReceiptData] = useState([]);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [loading, setLoading] = useState(false);

  /* ================= DONOR STATE ================= */
  const [donors, setDonors] = useState([]);
  const sewadarId = localStorage.getItem('sewadarId');
  /* ================= FORM VISIBILITY ================= */
  const [showForm, setShowForm] = useState(false);
  const location = useLocation();
  const donationId = location?.state?.donationId;

  /* ================= HEADER DATA ================= */
  const [header, setHeader] = useState({
    date: moment().format("YYYY-MM-DD"),
    formNo: "", // this will store ONLY the running number
    satsangPlace: "JAIPUR-I MC (BEELWA) (C/SC/SP)",
    attachedTo: "JAIPUR-I MC (BEELWA) (Center)",
    area: "Jaipur",
  });
  const getRefYear = (date) => {
    if (date) {
      return moment(date).year();
    }
    return moment().year();
  };

  const REF_BASE = "F.142/RSSB/JP";

  /* ================= ITEMS STATE ================= */
  const [items, setItems] = useState([]);

  /* ================= FETCH RECEIPTS ================= */
  const fetchVouchers = async () => {
    try {
      setLoading(true);
      const res = await apiClient.get(
        `${SERVER_BASE}/getReceiptVouchersForDonation/${centerCode}`
      );
      setReceiptData(res.data || []);
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to fetch receipt vouchers", "error");
    } finally {
      setLoading(false);
    }
  };
  const fetchDonationById = async (id) => {
    try {
      setLoading(true);
      const res = await apiClient.get(
        `${SERVER_BASE}/getDonation/${id}`
      );

      const donation = res.data;

      // populate header
      setHeader(donation.header || {});

      // populate items
      setItems(donation.items || []);

      // populate donors
      setDonors(donation.donors || []);

      // set selected receipt
      if (donation.receiptId) {
        setSelectedReceipt({
          value: donation.receiptId,
          label: "Loaded from saved donation",
        });
      }

      // open form
      setShowForm(true);
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to load donation", "error");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (donationId) {
      fetchDonationById(donationId);
    }
  }, [donationId]);

  useEffect(() => {
    fetchVouchers();
  }, []);

  /* ================= RECEIPT OPTIONS ================= */
  const receiptOptions = receiptData.map((r) => ({
    value: r.id,
    label: `${r.documentNo} - ${r.ledgerName} - ${r.date ? moment(r.date).format("DD MMM YY") : ""
      }`,
  }));

  /* ================= MAP ITEMS FROM RECEIPT (CREATE MODE ONLY) ================= */
  useEffect(() => {
    // ❌ DO NOTHING IN EDIT MODE
    if (donationId) return;
  
    if (!showForm || !selectedReceipt) return;
  
    const receipt = receiptData.find(
      (r) => r.id === selectedReceipt.value
    );
  
    if (receipt?.items?.length) {
      setItems(
        receipt.items.map((it, idx) => ({
          srNo: idx + 1,
          itemName: it.itemName || "",
          presentOffer: it.receivedQty || "",
          costOfItem: "",
          justification: "",
          existingQty: "",
          acquisitionDate: "",
        }))
      );
    }
  }, [showForm, selectedReceipt, receiptData, donationId]);
  
  useEffect(() => {
    // ❌ DO NOTHING IN EDIT MODE
    if (donationId) return;
  
    if (!showForm || !selectedReceipt) return;
  
    const receipt = receiptData.find(
      (r) => r.id === selectedReceipt.value
    );
  
    if (receipt) {
      setDonors([
        {
          name: receipt.ledgerName || "",
          mobile: receipt.sewadarSangatMobile || "",
          address: "",
        },
      ]);
    }
  }, [showForm, selectedReceipt, receiptData, donationId]);
  

  /* ================= HANDLERS ================= */
  const handleGenerate = () => {
    if (!selectedReceipt) {
      Swal.fire("Warning", "Please select a receipt", "warning");
      return;
    }
    setShowForm(true);
  };

  const handleHeaderChange = (field, value) => {
    setHeader((prev) => ({
      ...prev,
      [field]: value,
    }));
  };
  const handleSaveDonation = async () => {
    try {
      if (!selectedReceipt && !donationId) {
        Swal.fire("Error", "Receipt not selected", "error");
        return;
      }
  
      if (!items || items.length === 0) {
        Swal.fire("Error", "No items added", "error");
        return;
      }
  
      if (!donors || donors.length === 0) {
        Swal.fire("Error", "At least one donor is required", "error");
        return;
      }
  
      const payload = {
        receiptId: selectedReceipt?.value, // used only in create
        centerCode,
        header,
        items,
        donors,
        createdBy: sewadarId,
        updatedBy: sewadarId,
      };
  
      if (donationId) {
        // 🔁 UPDATE MODE
        await apiClient.put(
          `${SERVER_BASE}/updateDonation/${donationId}`,
          payload
        );
  
        Swal.fire("Success", "Donation updated successfully", "success");
      } else {
        // 🆕 CREATE MODE
        await apiClient.post(
          `${SERVER_BASE}/saveDonation`,
          payload
        );
  
        Swal.fire("Success", "Donation saved successfully", "success");
      }
    } catch (error) {
      console.error(error);
      Swal.fire(
        "Error",
        error?.response?.data?.message || "Failed to save donation",
        "error"
      );
    }
  };
  

  /* ================= UI ================= */
  return (
    <div className="formcontainer mt-4">
      <h5 className="mb-0">Donation Form</h5>

      <div className="row align-items-end mb-3">
        <div className="col-md-6">
          <label className="form-label fw-bold">
            Select Receipt Voucher
          </label>
          <Select
            options={receiptOptions}
            value={selectedReceipt}
            onChange={setSelectedReceipt}
            isLoading={loading}
            placeholder="Select receipt..."
            isClearable={!donationId}
            isDisabled={!!donationId}
          />

        </div>
        <div className="col-md-3">
          <button
            className="btn btn-success"
            onClick={handleGenerate}
            disabled={loading}
          >
            Generate Donation Form
          </button>
        </div>
      </div>

      {showForm && (
        <div className="card shadow-sm">
          <div className="card-body">
            {/* ================= HEADER ================= */}
            <div className="row mb-1">
              <div className="col-md-4">
                <label className="form-label fw-bold">Form No.</label>

                <div className="input-group">
                  <span className="input-group-text">
                    {`${REF_BASE}/${getRefYear(header.date)}/`}
                  </span>

                  <input
                    type="text"
                    className="form-control"
                    placeholder="Running No"
                    value={header.formNo}
                    onChange={(e) =>
                      handleHeaderChange("formNo", e.target.value)
                    }
                  />
                </div>
              </div>


              <div className="col-md-3">
                <label className="form-label fw-bold">Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={header.date}
                  onChange={(e) =>
                    handleHeaderChange("date", e.target.value)
                  }
                />

              </div>
            </div>

            <div className="row g-3 mb-4">
              <div className="col-md-4">
                <label className="form-label fw-bold">Area</label>
                <input
                  type="text"
                  className="form-control"
                  value={header.area}
                  onChange={(e) =>
                    handleHeaderChange("area", e.target.value)
                  }
                />
              </div>

              <div className="col-md-4">
                <label className="form-label fw-bold">
                  Name of Satsang Place
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={header.satsangPlace}
                  onChange={(e) =>
                    handleHeaderChange("satsangPlace", e.target.value)
                  }
                />
              </div>

              <div className="col-md-4">
                <label className="form-label fw-bold">Attached To</label>
                <input
                  type="text"
                  className="form-control"
                  value={header.attachedTo}
                  onChange={(e) =>
                    handleHeaderChange("attachedTo", e.target.value)
                  }
                />
              </div>
            </div>

            <hr />

            {/* ================= ITEMS & DONOR ================= */}
            <div className="row">
              {/* ITEMS */}
              <div className="col-md-7">
                <h6 className="fw-bold text-success mb-2">
                  Items / Donation
                </h6>

                <div
                  className="table-responsive"
                  style={{
                    border: "1px solid #dee2e6",
                    borderRadius: "6px",
                    padding: "8px",
                    backgroundColor: "#fff",
                  }}
                >
                  <table
                    className="table table-sm align-middle mb-0"
                    style={{
                      fontSize: "13px",
                      borderCollapse: "collapse",
                      width: "100%",
                    }}
                  >
                    <thead>
                      <tr style={{ backgroundColor: "#f8f9fa" }} className="align-middle">
                        <th
                          rowSpan="2"
                          className="text-center "
                          style={{ minWidth: "60px", border: "1px solid #dee2e6" }}
                        >
                          SR. NO.
                        </th>
                        <th
                          rowSpan="2"
                          style={{ minWidth: "180px", border: "1px solid #dee2e6" }}
                        >
                          NAME OF ARTICLE
                        </th>
                        <th
                          rowSpan="2"
                          className="text-center"
                          style={{ minWidth: "110px", border: "1px solid #dee2e6" }}
                        >
                          PRESENT OFFER
                        </th>
                        <th
                          rowSpan="2"
                          className="text-center"
                          style={{ minWidth: "110px", border: "1px solid #dee2e6" }}
                        >
                          COST OF ITEM
                        </th>
                        <th
                          rowSpan="2"
                          style={{ minWidth: "220px", border: "1px solid #dee2e6" }}
                        >
                          JUSTIFICATION FOR ACCEPTANCE / PURPOSE
                        </th>
                        <th
                          colSpan="2"
                          className="text-center"
                          style={{ border: "1px solid #dee2e6" }}
                        >
                          EXISTING HOLDING
                        </th>
                      </tr>
                      <tr style={{ backgroundColor: "#f8f9fa" }}>
                        <th
                          className="text-center"
                          style={{ minWidth: "100px", border: "1px solid #dee2e6" }}
                        >
                          QUANTITY
                        </th>
                        <th
                          className="text-center"
                          style={{ minWidth: "150px", border: "1px solid #dee2e6" }}
                        >
                          DATE OF ACQUISITION
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {items.length === 0 ? (
                        <tr>
                          <td
                            colSpan="7"
                            className="text-center text-muted"
                            style={{ border: "1px solid #dee2e6" }}
                          >
                            No items available
                          </td>
                        </tr>
                      ) : (
                        items.map((item, idx) => (
                          <tr key={idx}>
                            <td
                              className="text-center"
                              style={{ border: "1px solid #dee2e6" }}
                            >
                              {item.srNo}
                            </td>

                            <td
                              style={{
                                border: "1px solid #dee2e6",
                                whiteSpace: "normal",
                              }}
                            >
                              {item.itemName}
                            </td>

                            <td
                              className="text-center"
                              style={{ border: "1px solid #dee2e6" }}
                            >
                              {item.presentOffer}
                            </td>

                            <td style={{ border: "1px solid #dee2e6" }}>
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                value={item.costOfItem}
                                onChange={(e) => {
                                  const updated = [...items];
                                  updated[idx].costOfItem = e.target.value;
                                  setItems(updated);
                                }}
                              />
                            </td>

                            <td style={{ border: "1px solid #dee2e6" }}>
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                value={item.justification}
                                onChange={(e) => {
                                  const updated = [...items];
                                  updated[idx].justification = e.target.value;
                                  setItems(updated);
                                }}
                              />
                            </td>

                            <td style={{ border: "1px solid #dee2e6" }}>
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                value={item.existingQty}
                                onChange={(e) => {
                                  const updated = [...items];
                                  updated[idx].existingQty = e.target.value;
                                  setItems(updated);
                                }}
                              />
                            </td>

                            <td style={{ border: "1px solid #dee2e6" }}>
                              <input
                                type="date"
                                className="form-control form-control-sm"
                                value={item.acquisitionDate}
                                onChange={(e) => {
                                  const updated = [...items];
                                  updated[idx].acquisitionDate = e.target.value;
                                  setItems(updated);
                                }}
                              />
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>

                </div>
              </div>


              {/* DONOR */}
              <div className="col-md-5">
                <h6 className="fw-bold text-success mb-2">
                  Donor Details
                </h6>


                <div
                  className="table-responsive"
                  style={{
                    border: "1px solid #dee2e6",
                    borderRadius: "6px",
                    padding: "8px",
                    backgroundColor: "#fff",
                  }}
                >
                  <table
                    className="table table-sm align-middle mb-0"
                    style={{
                      fontSize: "13px",
                      borderCollapse: "collapse",
                      width: "100%",
                    }}
                  >
                    <thead>
                      <tr style={{ backgroundColor: "#f8f9fa" }}>
                        <th style={{ border: "1px solid #dee2e6" }}>Name</th>
                        <th style={{ border: "1px solid #dee2e6" }}>Mobile No</th>
                        <th style={{ border: "1px solid #dee2e6" }}>Address</th>
                        <th
                          style={{
                            width: "40px",
                            border: "1px solid #dee2e6",
                          }}
                        >Action</th>
                      </tr>
                    </thead>

                    <tbody>
                      {donors.map((donor, idx) => (
                        <tr key={idx}>
                          {/* NAME */}
                          <td style={{ border: "1px solid #dee2e6" }}>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={donor.name}
                              onChange={(e) => {
                                const updated = [...donors];
                                updated[idx].name = e.target.value;
                                setDonors(updated);
                              }}
                            />
                          </td>

                          {/* MOBILE */}
                          <td style={{ border: "1px solid #dee2e6" }}>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={donor.mobile}
                              onChange={(e) => {
                                const updated = [...donors];
                                updated[idx].mobile = e.target.value;
                                setDonors(updated);
                              }}
                            />
                          </td>

                          {/* ADDRESS */}
                          <td style={{ border: "1px solid #dee2e6" }}>
                            <textarea
                              className="form-control form-control-sm"
                              rows="2"
                              value={donor.address}
                              onChange={(e) => {
                                const updated = [...donors];
                                updated[idx].address = e.target.value;
                                setDonors(updated);
                              }}
                            />
                          </td>

                          {/* ACTIONS */}
                          <td
                            className="text-center"
                            style={{
                              border: "1px solid #dee2e6",
                              whiteSpace: "nowrap",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "center",
                                gap: "4px",
                              }}
                            >
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-success"
                                onClick={() =>
                                  setDonors([
                                    ...donors,
                                    { name: "", mobile: "", address: "" },
                                  ])
                                }
                              >
                                +
                              </button>

                              {donors.length > 1 && (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => {
                                    const updated = donors.filter(
                                      (_, i) => i !== idx
                                    );
                                    setDonors(updated);
                                  }}
                                >
                                  −
                                </button>
                              )}
                            </div>
                          </td>

                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>


            </div>

            <div className="text-end mt-4">
<button className="btn btn-primary" onClick={handleSaveDonation}>
{donationId ? "Update Donation Form" : "Save Donation Form"}
</button>


            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DonationForm;
